import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal, ModalOverlay, ModalContent,
  Box, Flex, Text, Icon, Button, HStack, VStack,
  Circle, Badge, useColorModeValue, SimpleGrid,
  Progress,
} from '@chakra-ui/react';
import {
  MdBuild, MdAddTask, MdVerifiedUser, MdAdminPanelSettings, MdWater,
  MdCheckCircle, MdPictureAsPdf, MdMap,
  MdMenuBook, MdThumbUp, MdChevronRight, MdChevronLeft,
  MdFilterList, MdEdit, MdLocationOn, MdCameraAlt, MdThumbDown,
  MdBarChart, MdPerson, MdHome, MdList,
} from 'react-icons/md';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../views/admin/auth/AuthProvider';
import { useNavigate } from 'react-router-dom';

function getSteps(role, color) {
  const tecnico = [
    {
      label: 'Suas OS',
      icon: MdList,
      heading: 'Acompanhe suas Ordens de Serviço',
      desc: 'Na tela Início você vê todas as suas OS com o status atual. Toque em qualquer linha para abrir os detalhes.',
      tip: 'OS aprovadas ficam bloqueadas — não é possível editar após aprovação.',
      mock: (
        <VStack align="stretch" spacing="5px">
          {[
            { num: '2024/012', tipo: 'Fumaça',  status: 'Aprovada ✓',    c: 'green'  },
            { num: '2024/013', tipo: 'Corante', status: 'Ag. Validação',  c: 'orange' },
            { num: '2024/014', tipo: 'Fumaça',  status: 'Rascunho',       c: 'gray'   },
          ].map(r => (
            <Flex key={r.num} bg="white" borderRadius="8px" px="8px" py="6px"
              justify="space-between" align="center" boxShadow="sm">
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="10px" fontWeight="700" color="gray.700">{r.num}</Text>
                <Text fontSize="9px" color="gray.400">{r.tipo}</Text>
              </VStack>
              <Badge colorScheme={r.c} fontSize="8px" borderRadius="full" px="6px">{r.status}</Badge>
            </Flex>
          ))}
        </VStack>
      ),
    },
    {
      label: 'Nova OS',
      icon: MdAddTask,
      heading: 'Registre um novo atendimento',
      desc: 'Toque em "+ Nova OS", preencha os dados do serviço, capture a localização GPS e anexe as fotos do local.',
      tip: 'Use o GPS para capturar coordenadas automaticamente — mais preciso do que digitar o endereço.',
      mock: (
        <VStack align="stretch" spacing="6px">
          <Box bg="white" borderRadius="8px" p="8px">
            <Text fontSize="9px" color="gray.400" mb="2px">Tipo de Serviço</Text>
            <Flex justify="space-between" align="center">
              <Text fontSize="10px" fontWeight="600" color="gray.700">Teste de Fumaça</Text>
              <Icon as={MdChevronRight} color="gray.400" boxSize="12px"/>
            </Flex>
          </Box>
          <HStack gap="6px">
            <Box flex={1} bg="blue.500" borderRadius="8px" p="6px" textAlign="center">
              <Icon as={MdLocationOn} color="white" boxSize="14px"/>
              <Text fontSize="9px" color="white" mt="1px">GPS</Text>
            </Box>
            <Box flex={1} bg="purple.500" borderRadius="8px" p="6px" textAlign="center">
              <Icon as={MdCameraAlt} color="white" boxSize="14px"/>
              <Text fontSize="9px" color="white" mt="1px">Fotos</Text>
            </Box>
          </HStack>
          <HStack gap="6px">
            <Box flex={1} bg="gray.100" borderRadius="8px" p="6px" textAlign="center">
              <Text fontSize="9px" color="gray.500" fontWeight="600">Rascunho</Text>
            </Box>
            <Box flex={1} bg="blue.500" borderRadius="8px" p="6px" textAlign="center">
              <Text fontSize="9px" color="white" fontWeight="600">Enviar</Text>
            </Box>
          </HStack>
        </VStack>
      ),
    },
    {
      label: 'Formulário',
      icon: MdBuild,
      heading: 'Preencha o formulário técnico',
      desc: 'Cada tipo de serviço tem um formulário específico. Fumaça pede PVs e extensões; Corante pede dados do ramal e morador.',
      tip: 'Você pode salvar como rascunho e continuar mais tarde — os dados ficam salvos na nuvem.',
      mock: (
        <VStack align="stretch" spacing="5px">
          <Box bg="blue.50" borderRadius="8px" p="7px">
            <Text fontSize="9px" fontWeight="700" color="blue.600" mb="4px">SINGULARIDADES (PVs)</Text>
            {['PV Inicial', 'PV Final', 'Extensão (m)'].map(f => (
              <Box key={f} bg="white" borderRadius="5px" px="7px" py="4px" mb="3px">
                <Text fontSize="9px" color="gray.400">{f}</Text>
              </Box>
            ))}
          </Box>
          <Box bg="green.50" borderRadius="8px" p="7px">
            <Text fontSize="9px" fontWeight="700" color="green.600" mb="4px">RESULTADO</Text>
            <HStack gap="6px">
              {['Aprovado','Reprovado'].map(s => (
                <Box key={s} flex={1} borderRadius="5px" bg={s==='Aprovado'?'green.400':'gray.100'} px="6px" py="4px" textAlign="center">
                  <Text fontSize="9px" color={s==='Aprovado'?'white':'gray.500'} fontWeight="600">{s}</Text>
                </Box>
              ))}
            </HStack>
          </Box>
        </VStack>
      ),
    },
    {
      label: 'Validação',
      icon: MdVerifiedUser,
      heading: 'Acompanhe a validação',
      desc: 'Após enviar, a OS vai para o supervisor revisar. Aprovada = bloqueada ✓. Reprovada = você recebe um comentário.',
      tip: 'Se reprovada, leia o comentário do supervisor, corrija os dados e reenvie.',
      mock: (
        <VStack align="stretch" spacing="5px">
          {[
            { label: 'Criada pelo técnico',    done: true  },
            { label: 'Enviada para validação',  done: true  },
            { label: 'Em análise',              done: true  },
            { label: 'Aprovada ✓',             done: true, last: true },
          ].map((s, i) => (
            <HStack key={i} gap="8px">
              <Circle size="20px" bg={s.done ? 'green.500' : 'gray.200'} flexShrink={0}>
                <Icon as={MdCheckCircle} boxSize="12px" color={s.done ? 'white' : 'gray.400'}/>
              </Circle>
              <Text fontSize="9px" fontWeight={s.last ? '700' : '400'}
                color={s.done ? 'gray.700' : 'gray.400'}>{s.label}</Text>
            </HStack>
          ))}
          <Box bg="green.50" borderRadius="8px" p="7px" mt="2px">
            <Text fontSize="9px" color="green.700" fontWeight="600">OS aprovada e bloqueada</Text>
          </Box>
        </VStack>
      ),
    },
  ];

  const supervisor = [
    {
      label: 'Validações',
      icon: MdVerifiedUser,
      heading: 'Central de Validações',
      desc: 'Veja todas as OS que técnicos enviaram para aprovação. O número de pendentes aparece em destaque no topo.',
      tip: 'Filtre por técnico ou tipo de serviço para organizar a fila de validação.',
      mock: (
        <VStack align="stretch" spacing="5px">
          <HStack justify="space-between" mb="2px">
            <Text fontSize="10px" fontWeight="700" color="orange.600">Aguardando (3)</Text>
            <Badge colorScheme="orange" fontSize="8px">Pendente</Badge>
          </HStack>
          {[
            { num: '2024/011', tec: 'João S.',  tipo: 'Fumaça'  },
            { num: '2024/012', tec: 'Maria L.', tipo: 'Corante' },
            { num: '2024/013', tec: 'Pedro M.', tipo: 'Fumaça'  },
          ].map(r => (
            <Flex key={r.num} bg="white" borderRadius="8px" px="8px" py="6px"
              justify="space-between" align="center" boxShadow="sm">
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="10px" fontWeight="700">{r.num}</Text>
                <Text fontSize="9px" color="gray.400">{r.tec} · {r.tipo}</Text>
              </VStack>
              <HStack gap="4px">
                <Box bg="green.500" borderRadius="6px" w="22px" h="22px"
                  display="flex" alignItems="center" justifyContent="center">
                  <Icon as={MdThumbUp} boxSize="11px" color="white"/>
                </Box>
                <Box bg="red.500" borderRadius="6px" w="22px" h="22px"
                  display="flex" alignItems="center" justifyContent="center">
                  <Icon as={MdThumbDown} boxSize="11px" color="white"/>
                </Box>
              </HStack>
            </Flex>
          ))}
        </VStack>
      ),
    },
    {
      label: 'Analisar',
      icon: MdEdit,
      heading: 'Analise a OS completa',
      desc: 'Toque na OS para abrir os detalhes. Veja localização, dados do imóvel e o formulário técnico preenchido.',
      tip: 'A aba "Dados Técnicos" mostra o formulário técnico completo em modo leitura.',
      mock: (
        <VStack align="stretch" spacing="5px">
          <Box bg="white" borderRadius="10px" overflow="hidden" boxShadow="md">
            <Box bg="orange.500" px="10px" py="7px">
              <Text fontSize="10px" fontWeight="700" color="white">OS 2024/011 — Fumaça</Text>
            </Box>
            <Box px="10px" py="7px">
              <HStack gap="6px" mb="5px">
                <Box flex={1} bg="orange.50" borderRadius="6px" px="6px" py="4px" textAlign="center">
                  <Text fontSize="9px" color="orange.700" fontWeight="600">Detalhes</Text>
                </Box>
                <Box flex={1} bg="blue.50" borderRadius="6px" px="6px" py="4px" textAlign="center">
                  <Text fontSize="9px" color="blue.700" fontWeight="600">Técnico</Text>
                </Box>
              </HStack>
              {[['Técnico','João Silva'],['CEP','12345-000'],['GPS','✓ Capturado']].map(([l,v])=>(
                <Flex key={l} justify="space-between" mb="3px">
                  <Text fontSize="9px" color="gray.400">{l}:</Text>
                  <Text fontSize="9px" fontWeight="600" color="gray.700">{v}</Text>
                </Flex>
              ))}
            </Box>
          </Box>
        </VStack>
      ),
    },
    {
      label: 'Aprovar',
      icon: MdCheckCircle,
      heading: 'Aprovar ou Reprovar',
      desc: 'Aprove direto na lista ou entre na OS para reprovar com comentário.',
      tip: 'Sempre deixe um comentário ao reprovar — o técnico precisa saber o que corrigir.',
      mock: (
        <VStack align="stretch" spacing="6px">
          <Box bg="green.50" border="1px solid" borderColor="green.200" borderRadius="8px" p="8px">
            <Text fontSize="9px" fontWeight="700" color="green.700" mb="4px">Aprovar OS</Text>
            <Text fontSize="9px" color="green.600">OS será aprovada e bloqueada para edição.</Text>
            <Box bg="green.500" borderRadius="6px" mt="6px" py="5px" textAlign="center">
              <Text fontSize="9px" color="white" fontWeight="700">Confirmar Aprovação</Text>
            </Box>
          </Box>
          <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="8px" p="8px">
            <Text fontSize="9px" fontWeight="700" color="red.700" mb="4px">Reprovar OS</Text>
            <Box bg="white" border="1px solid" borderColor="red.200" borderRadius="5px" px="7px" py="4px" mb="5px">
              <Text fontSize="9px" color="gray.400">Motivo da reprovação...</Text>
            </Box>
            <Box bg="red.500" borderRadius="6px" py="5px" textAlign="center">
              <Text fontSize="9px" color="white" fontWeight="700">Confirmar Reprovação</Text>
            </Box>
          </Box>
        </VStack>
      ),
    },
    {
      label: 'Relatórios',
      icon: MdPictureAsPdf,
      heading: 'Gere relatórios e use o mapa',
      desc: 'Você também tem acesso ao Painel Admin: gere PDFs, veja o mapa de OS com pins GPS e filtre por qualquer critério.',
      tip: 'O PDF abre direto no navegador — selecione o modelo PG Soluções ou Norbrasil.',
      mock: (
        <VStack align="stretch" spacing="5px">
          <SimpleGrid columns={2} gap="5px">
            {[
              { icon: MdBarChart,     label: 'Painel Admin', c: 'purple' },
              { icon: MdMap,          label: 'Mapa de OS',   c: 'teal'   },
              { icon: MdPictureAsPdf, label: 'Gerar PDF',    c: 'green'  },
              { icon: MdFilterList,   label: 'Filtros',      c: 'blue'   },
            ].map(i => (
              <Flex key={i.label} bg={`${i.c}.50`} border="1px solid"
                borderColor={`${i.c}.200`} borderRadius="8px"
                p="8px" align="center" gap="6px">
                <Circle size="22px" bg={`${i.c}.500`}>
                  <Icon as={i.icon} boxSize="12px" color="white"/>
                </Circle>
                <Text fontSize="9px" fontWeight="600" color={`${i.c}.700`}>{i.label}</Text>
              </Flex>
            ))}
          </SimpleGrid>
        </VStack>
      ),
    },
  ];

  const admin = [
    {
      label: 'Painel',
      icon: MdAdminPanelSettings,
      heading: 'Painel Administrativo',
      desc: 'Visão completa: KPIs de todas as OS, gráficos de desempenho, filtros avançados e tabela com todas as ordens.',
      tip: 'Clique no cabeçalho de qualquer coluna da tabela para ordenar os dados.',
      mock: (
        <VStack align="stretch" spacing="5px">
          <SimpleGrid columns={2} gap="5px">
            {[
              { l: 'Total',      v: '47', c: 'blue'   },
              { l: 'Aguardando', v: '8',  c: 'orange' },
              { l: 'Aprovadas',  v: '35', c: 'green'  },
              { l: 'Reprovadas', v: '4',  c: 'red'    },
            ].map(s => (
              <Box key={s.l} bg={`${s.c}.50`} border="1px solid"
                borderColor={`${s.c}.200`} borderRadius="8px" p="7px" textAlign="center">
                <Text fontSize="16px" fontWeight="800" color={`${s.c}.500`}>{s.v}</Text>
                <Text fontSize="8px" color="gray.500">{s.l}</Text>
              </Box>
            ))}
          </SimpleGrid>
          <Box bg="purple.50" borderRadius="8px" h="40px"
            display="flex" alignItems="center" justifyContent="center">
            <Icon as={MdBarChart} color="purple.300" boxSize="24px"/>
          </Box>
        </VStack>
      ),
    },
    {
      label: 'Filtros',
      icon: MdFilterList,
      heading: 'Filtrar e buscar qualquer OS',
      desc: 'Busque por técnico, nº da OS, tipo de serviço, período ou status. Os resultados atualizam em tempo real.',
      tip: 'Combine múltiplos filtros — ex: "Fumaça + Ag. Validação + últimos 30 dias".',
      mock: (
        <VStack align="stretch" spacing="5px">
          {['Buscar por nº ou técnico...','Período','Tipo de Serviço','Status'].map(f => (
            <Box key={f} bg="white" borderRadius="7px" px="8px" py="5px"
              border="1px solid" borderColor="gray.200">
              <Text fontSize="9px" color="gray.400">{f}</Text>
            </Box>
          ))}
          <Box bg="purple.500" borderRadius="7px" py="6px" textAlign="center">
            <Text fontSize="9px" color="white" fontWeight="700">Aplicar Filtros</Text>
          </Box>
        </VStack>
      ),
    },
    {
      label: 'Editar',
      icon: MdEdit,
      heading: 'Edite qualquer OS',
      desc: 'Admin pode editar OS mesmo após aprovadas — dados do imóvel, formulário técnico e coordenadas GPS.',
      tip: 'Diferente do técnico, você não é bloqueado pelo status "aprovada".',
      mock: (
        <VStack align="stretch" spacing="5px">
          <Box bg="white" borderRadius="10px" overflow="hidden" boxShadow="sm">
            <Box bg="purple.500" px="10px" py="7px">
              <HStack justify="space-between">
                <Text fontSize="10px" color="white" fontWeight="700">OS 2024/015</Text>
                <Badge bg="whiteAlpha.300" color="white" fontSize="8px">Admin</Badge>
              </HStack>
            </Box>
            <Box px="10px" py="7px">
              {[['Status','Aprovada ✓'],['Tipo','Fumaça'],['GPS','✓'],['Técnico','João']].map(([l,v])=>(
                <Flex key={l} justify="space-between" mb="3px"
                  bg={l==='Status'?'green.50':'transparent'}
                  borderRadius="5px" px={l==='Status'?'5px':0}>
                  <Text fontSize="9px" color="gray.400">{l}:</Text>
                  <Text fontSize="9px" fontWeight="700" color={l==='Status'?'green.600':'gray.700'}>{v}</Text>
                </Flex>
              ))}
              <Box bg="blue.500" borderRadius="6px" mt="6px" py="5px" textAlign="center">
                <HStack justify="center" gap="4px">
                  <Icon as={MdEdit} boxSize="10px" color="white"/>
                  <Text fontSize="9px" color="white" fontWeight="700">Editar OS</Text>
                </HStack>
              </Box>
            </Box>
          </Box>
        </VStack>
      ),
    },
    {
      label: 'PDF + Mapa',
      icon: MdMap,
      heading: 'Relatórios PDF e Mapa',
      desc: 'Gere PDFs formatados (PG Soluções ou Norbrasil) direto no navegador. O mapa mostra pins coloridos por status.',
      tip: 'Pins verdes = aprovadas, laranja = aguardando, cinza = rascunho. Clique no pin para detalhes.',
      mock: (
        <VStack align="stretch" spacing="5px">
          <HStack gap="5px">
            {['PG Soluções','Norbrasil'].map(m => (
              <Box key={m} flex={1} bg={m==='PG Soluções'?'green.500':'blue.500'}
                borderRadius="8px" py="8px" textAlign="center">
                <Icon as={MdPictureAsPdf} boxSize="14px" color="white"/>
                <Text fontSize="8px" color="white" fontWeight="700" mt="2px">{m}</Text>
              </Box>
            ))}
          </HStack>
          <Box bg="teal.50" border="1px solid" borderColor="teal.200"
            borderRadius="8px" h="80px" position="relative" overflow="hidden">
            <Text fontSize="9px" color="teal.400" position="absolute" top="4px" left="8px">Mapa interativo</Text>
            {[
              { top: '25px', left: '20px',  c: 'green'  },
              { top: '15px', left: '80px',  c: 'orange' },
              { top: '45px', left: '130px', c: 'green'  },
              { top: '30px', left: '180px', c: 'red'    },
            ].map((p, i) => (
              <Circle key={i} size="12px" bg={`${p.c}.500`}
                position="absolute" top={p.top} left={p.left}
                border="2px solid white" boxShadow="0 1px 4px rgba(0,0,0,0.3)"/>
            ))}
          </Box>
        </VStack>
      ),
    },
  ];

  const cliente = [
    {
      label: 'Status',
      icon: MdHome,
      heading: 'Acompanhe seus serviços',
      desc: 'Veja as Ordens de Serviço vinculadas ao seu endereço com o status atual.',
      tip: 'As informações atualizam automaticamente — não precisa recarregar a página.',
      mock: (
        <VStack align="stretch" spacing="5px">
          {[
            { num: '2024/011', tipo: 'Inspeção de Rede', status: 'Em andamento', c: 'blue'  },
            { num: '2024/012', tipo: 'Teste de Fumaça',  status: 'Concluída ✓',  c: 'green' },
          ].map(r => (
            <Box key={r.num} bg="white" borderRadius="8px" px="8px" py="8px" boxShadow="sm">
              <Flex justify="space-between" align="flex-start">
                <VStack align="flex-start" spacing={0}>
                  <Text fontSize="10px" fontWeight="700">{r.num}</Text>
                  <Text fontSize="9px" color="gray.400">{r.tipo}</Text>
                </VStack>
                <Badge colorScheme={r.c} fontSize="8px" borderRadius="full">{r.status}</Badge>
              </Flex>
            </Box>
          ))}
        </VStack>
      ),
    },
    {
      label: 'Detalhes',
      icon: MdList,
      heading: 'Detalhes de cada OS',
      desc: 'Toque em uma OS para ver data, tipo de serviço, técnico responsável e observações do atendimento.',
      tip: 'O histórico fica sempre disponível — todas as OS passadas aparecem na lista.',
      mock: (
        <Box bg="white" borderRadius="10px" overflow="hidden" boxShadow="sm">
          <Box bg="teal.500" px="10px" py="7px">
            <Text fontSize="10px" fontWeight="700" color="white">OS 2024/012 — Fumaça</Text>
          </Box>
          <Box px="10px" py="7px">
            {[
              ['Data','08/04/2026'],
              ['Técnico','João Silva'],
              ['Status','Concluída ✓'],
              ['Observação','Trecho vistoriado'],
            ].map(([l,v]) => (
              <Flex key={l} justify="space-between" mb="4px">
                <Text fontSize="9px" color="gray.400">{l}:</Text>
                <Text fontSize="9px" fontWeight="600" color={l==='Status'?'green.600':'gray.700'}>{v}</Text>
              </Flex>
            ))}
          </Box>
        </Box>
      ),
    },
  ];

  const map = { tecnico, supervisor, admin, cliente };
  return map[role] || tecnico;
}

const ROLE_META = {
  tecnico:    { label: 'Técnico',        color: 'blue',   icon: MdBuild,              greeting: 'Bem-vindo ao SGOS' },
  supervisor: { label: 'Supervisor',     color: 'orange', icon: MdVerifiedUser,       greeting: 'Bem-vindo ao SGOS' },
  admin:      { label: 'Administrador',  color: 'purple', icon: MdAdminPanelSettings, greeting: 'Bem-vindo ao SGOS' },
  cliente:    { label: 'Cliente',        color: 'teal',   icon: MdWater,              greeting: 'Bem-vindo ao SGOS' },
};

function MockBottomBar({ color }) {
  const bg = useColorModeValue('white', 'navy.800');
  return (
    <Flex bg={bg} borderTop="1px solid" borderColor="gray.100"
      px="8px" py="6px" justify="space-around">
      {[
        { icon: MdHome,    label: 'Início'  },
        { icon: MdAddTask, label: 'Nova OS' },
        { icon: MdPerson,  label: 'Perfil'  },
      ].map((t, i) => (
        <VStack key={t.label} spacing="1px">
          <Icon as={t.icon} boxSize="14px"
            color={i === 0 ? `${color}.500` : 'gray.300'}/>
          <Text fontSize="7px"
            color={i === 0 ? `${color}.500` : 'gray.400'}
            fontWeight={i === 0 ? '700' : '400'}>{t.label}</Text>
        </VStack>
      ))}
    </Flex>
  );
}

export default function ManualOnboarding() {
  const { user, role, ready } = useAuth();
  const navigate               = useNavigate();
  const [show, setShow]        = useState(false);
  const [step, setStep]        = useState(0);
  const [loading, setLoading]  = useState(false);

  const r     = role || 'tecnico';
  const meta  = ROLE_META[r] || ROLE_META.tecnico;
  const steps = getSteps(r, meta.color);
  const total = steps.length;
  const pct   = Math.round(((step + 1) / total) * 100);

  const overlayBg = useColorModeValue('gray.50', 'navy.900');
  const cardBg    = useColorModeValue('white',   'navy.800');
  const mockBg    = useColorModeValue('gray.50', 'navy.900');
  const divider   = useColorModeValue('gray.100','gray.700');
  const subText   = useColorModeValue('gray.500','gray.400');
  const dotOff    = useColorModeValue('whiteAlpha.400','whiteAlpha.300');
  const tipBg     = useColorModeValue(`${meta.color}.50`,  `${meta.color}.900`);
  const tipBorder = useColorModeValue(`${meta.color}.200`, `${meta.color}.700`);
  const tipColor  = useColorModeValue(`${meta.color}.700`, `${meta.color}.200`);

  useEffect(() => {
    if (!ready || !user?.uid) return;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        const data = snap.exists() ? snap.data() : {};
        if (!data.manualVisto) setShow(true);
      } catch (e) { console.warn('ManualOnboarding:', e); }
    })();
  }, [ready, user?.uid]);

  const confirmar = useCallback(async () => {
    setLoading(true);
    try {
      if (user?.uid) await updateDoc(doc(db, 'users', user.uid), { manualVisto: true });
    } catch (e) { console.warn('ManualOnboarding:', e); }
    setShow(false);
    setLoading(false);
  }, [user?.uid]);

  const abrirManual = useCallback(() => {
    setShow(false);
    navigate('/admin/manual');
  }, [navigate]);

  const prev = () => setStep(s => Math.max(0, s - 1));
  const next = () => {
    if (step < total - 1) setStep(s => s + 1);
    else confirmar();
  };

  if (!show) return null;

  const current = steps[step];

  return (
    <Modal
      isOpen
      onClose={() => {}}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      size="full"
      motionPreset="slideInBottom"
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(6px)" />
      <ModalContent
        borderRadius={{ base: '0', md: '24px' }}
        m={{ base: '0', md: '40px auto' }}
        maxH={{ base: '100dvh', md: 'calc(100vh - 80px)' }}
        maxW={{ base: '100%', md: '440px' }}
        overflow="hidden"
        bg={overlayBg}
        display="flex"
        flexDirection="column"
      >

        {/* ── Topo colorido ──────────────────────────────────────────────── */}
        <Box
          bg={`${meta.color}.500`}
          px={{ base: '18px', md: '24px' }}
          pt={{ base: 'env(safe-area-inset-top, 16px)', md: '20px' }}
          pb="14px"
          flexShrink={0}
        >
          <HStack justify="space-between" mb="10px">
            <HStack gap="10px">
              <Circle size="38px" bg="whiteAlpha.300" flexShrink={0}>
                <Icon as={meta.icon} boxSize="18px" color="white"/>
              </Circle>
              <Box>
                <Text fontWeight="700" fontSize="sm" color="white" lineHeight={1.2}>
                  {meta.greeting}
                </Text>
                <Badge bg="whiteAlpha.300" color="white" fontSize="8px"
                  borderRadius="full" px="7px" mt="2px">{meta.label}</Badge>
              </Box>
            </HStack>
            <Text fontSize="11px" color="whiteAlpha.800" fontWeight="700" flexShrink={0}>
              {step + 1} / {total}
            </Text>
          </HStack>

          {/* Barra de progresso */}
          <Progress value={pct} size="xs" borderRadius="full"
            bg="whiteAlpha.300" colorScheme="whiteAlpha"
            sx={{ '& > div': { transition: 'width 0.3s ease' } }}/>

          {/* Dots clicáveis */}
          <HStack justify="center" gap="6px" mt="10px">
            {steps.map((_, i) => (
              <Box key={i}
                w={i === step ? '22px' : '7px'} h="7px"
                borderRadius="full"
                bg={i === step ? 'white' : dotOff}
                transition="all 0.25s"
                cursor="pointer"
                onClick={() => setStep(i)}
              />
            ))}
          </HStack>
        </Box>

        {/* ── Corpo rolável ───────────────────────────────────────────────── */}
        <Box flex={1} overflowY="auto" WebkitOverflowScrolling="touch">
          <Box px={{ base: '16px', md: '24px' }} py="18px">

            {/* Título */}
            <HStack gap="10px" mb="8px">
              <Circle size="34px" bg={`${meta.color}.500`} flexShrink={0}>
                <Icon as={current.icon} boxSize="16px" color="white"/>
              </Circle>
              <Text fontWeight="700" fontSize={{ base: 'md', md: 'lg' }} lineHeight={1.3}>
                {current.heading}
              </Text>
            </HStack>

            {/* Descrição */}
            <Text fontSize="sm" color={subText} lineHeight="1.6" mb="16px">
              {current.desc}
            </Text>

            {/* Phone mockup */}
            <Box
              bg={cardBg}
              borderRadius="22px"
              overflow="hidden"
              border="2.5px solid"
              borderColor={divider}
              maxW={{ base: '255px', md: '270px' }}
              mx="auto"
              mb="16px"
              boxShadow="0 16px 48px rgba(0,0,0,0.14)"
            >
              {/* Status bar */}
              <Box bg={`${meta.color}.500`} px="12px" pt="8px" pb="6px">
                <HStack justify="space-between" mb="3px">
                  <Text fontSize="8px" color="white" fontWeight="700">9:41</Text>
                  <HStack gap="3px">
                    <Box w="10px" h="5px" bg="white" borderRadius="1px" opacity={0.85}/>
                    <Box w="5px" h="5px" bg="white" borderRadius="full" opacity={0.85}/>
                  </HStack>
                </HStack>
                <Text fontSize="10px" fontWeight="700" color="white" noOfLines={1}>
                  SGOS — {current.label}
                </Text>
              </Box>
              {/* Screen */}
              <Box bg={mockBg} px="10px" py="10px" minH="150px">
                {current.mock}
              </Box>
              {/* Bottom nav simulada */}
              <MockBottomBar color={meta.color} />
            </Box>

            {/* Dica */}
            <Box bg={tipBg} border="1px solid" borderColor={tipBorder}
              borderRadius="12px" px="14px" py="10px">
              <Text fontSize="xs" color={tipColor} lineHeight="1.5">
                <Text as="span" fontWeight="700">Dica: </Text>{current.tip}
              </Text>
            </Box>

          </Box>
        </Box>

        {/* ── Rodapé fixo ─────────────────────────────────────────────────── */}
        <Box
          px={{ base: '16px', md: '24px' }}
          pt="12px"
          pb={{ base: 'calc(env(safe-area-inset-bottom, 0px) + 12px)', md: '16px' }}
          borderTop="1px solid"
          borderColor={divider}
          bg={cardBg}
          flexShrink={0}
        >
          <HStack justify="space-between" gap="10px">
            {step === 0 ? (
              <Button variant="ghost" size="sm" color={subText} fontSize="11px"
                onClick={abrirManual} px={2}
                _hover={{ textDecoration: 'underline', bg: 'transparent' }}>
                Manual completo
              </Button>
            ) : (
              <Button variant="outline" size="sm" onClick={prev}
                borderRadius="10px" minW="80px"
                leftIcon={<Icon as={MdChevronLeft}/>}>
                Voltar
              </Button>
            )}

            <Button
              colorScheme={meta.color}
              size="md"
              borderRadius="12px"
              px="24px"
              h="44px"
              fontWeight="700"
              fontSize="sm"
              isLoading={loading}
              loadingText="Salvando..."
              onClick={next}
              rightIcon={
                step < total - 1
                  ? <Icon as={MdChevronRight}/>
                  : <Icon as={MdCheckCircle}/>
              }
            >
              {step < total - 1 ? 'Próximo' : 'Entendi'}
            </Button>
          </HStack>

          <Text fontSize="10px" color={subText} textAlign="center" mt="8px">
            Passo {step + 1} de {total} — {current.label}
          </Text>
        </Box>

      </ModalContent>
    </Modal>
  );
}
